import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Plus, KeyRound, Webhook, Trash2, Loader2, Copy, Check, Send, AlertTriangle,
} from 'lucide-react';
import { TopBanner } from '@/components/TopBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { API_SCOPES, WEBHOOK_EVENTS } from '@/api/integrations.api';
import {
  useApiKeys, useCreateApiKey, useRevokeApiKey,
  useWebhooks, useCreateWebhook, useDeleteWebhook, useTestWebhook,
} from '@/hooks/useIntegrations';

export const Route = createFileRoute('/_authenticated/integraciones')({
  component: IntegracionesPage,
});

/** Muestra un secreto que el backend devuelve UNA sola vez, con boton de copiar. */
function SecretOnce({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Guardalo ahora: <strong>no se vuelve a mostrar</strong>. Solo queda su hash en el servidor.</span>
      </div>
      <Label>{label}</Label>
      <div className="flex gap-2">
        <code className="flex-1 overflow-x-auto rounded bg-muted px-3 py-2 text-xs">{value}</code>
        <Button type="button" variant="outline" size="icon" onClick={copy} title="Copiar">
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

/** Lista de checkboxes nativa (no hay primitiva checkbox en el design system). */
function MultiSelect({
  options, selected, onToggle,
}: { options: readonly string[]; selected: string[]; onToggle: (v: string) => void }) {
  return (
    <div className="grid max-h-48 gap-1 overflow-y-auto rounded-md border p-2">
      {options.map((o) => (
        <label key={o} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted">
          <input
            type="checkbox"
            className="h-4 w-4 accent-primary"
            checked={selected.includes(o)}
            onChange={() => onToggle(o)}
          />
          <code className="text-xs">{o}</code>
        </label>
      ))}
    </div>
  );
}

function IntegracionesPage() {
  const { data: keys = [], isLoading: loadingKeys } = useApiKeys();
  const { data: hooks = [], isLoading: loadingHooks } = useWebhooks();
  const createKey = useCreateApiKey();
  const revokeKey = useRevokeApiKey();
  const createHook = useCreateWebhook();
  const deleteHook = useDeleteWebhook();
  const testHook = useTestWebhook();

  const [keyOpen, setKeyOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [keyScopes, setKeyScopes] = useState<string[]>([...API_SCOPES]);
  const [newKey, setNewKey] = useState<string | null>(null);

  const [hookOpen, setHookOpen] = useState(false);
  const [hookUrl, setHookUrl] = useState('');
  const [hookEvents, setHookEvents] = useState<string[]>([...WEBHOOK_EVENTS]);
  const [newSecret, setNewSecret] = useState<string | null>(null);

  const toggle = (arr: string[], set: (v: string[]) => void, v: string) =>
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const submitKey = async () => {
    if (!keyName.trim() || !keyScopes.length) return;
    const res = await createKey.mutateAsync({ name: keyName.trim(), scopes: keyScopes });
    setNewKey(res.key);
    setKeyName('');
  };

  const submitHook = async () => {
    if (!hookUrl.trim() || !hookEvents.length) return;
    const res = await createHook.mutateAsync({ url: hookUrl.trim(), events: hookEvents });
    setNewSecret(res.secret);
    setHookUrl('');
  };

  const closeKeyDialog = () => { setKeyOpen(false); setNewKey(null); };
  const closeHookDialog = () => { setHookOpen(false); setNewSecret(null); };

  return (
    <>
      <TopBanner
        title="Integraciones"
        subtitle="API keys y webhooks para que otra plataforma consuma el chat"
      />

      <div className="flex-1 space-y-8 overflow-auto p-6">
        {/* ---------- API keys ---------- */}
        <section>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <KeyRound className="h-5 w-5" /> API keys
              </h2>
              <p className="text-sm text-muted-foreground">
                Credenciales server-to-server. Nunca las pongas en el navegador.
              </p>
            </div>
            <Button onClick={() => setKeyOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Nueva key
            </Button>
          </div>

          {loadingKeys ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando...
            </div>
          ) : keys.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
              <KeyRound className="mx-auto mb-2 h-7 w-7 opacity-50" />
              Sin keys todavia. Crea la primera para que un integrador consuma la API.
            </div>
          ) : (
            <div className="grid gap-3">
              {keys.map((k) => (
                <Card key={k.id} className={k.revokedAt ? 'opacity-60' : undefined}>
                  <CardContent className="flex flex-wrap items-center gap-3 p-4">
                    <code className="rounded bg-muted px-2 py-1 text-sm font-bold">{k.prefix}...</code>
                    <span className="font-medium">{k.name}</span>
                    {k.revokedAt ? (
                      <Badge variant="destructive">Revocada</Badge>
                    ) : (
                      <Badge variant="success">Activa</Badge>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {k.scopes.map((s) => (
                        <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                      ))}
                    </div>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {k.lastUsedAt
                        ? `ultimo uso ${new Date(k.lastUsedAt).toLocaleString('es-AR')}`
                        : 'sin usar'}
                    </span>
                    {!k.revokedAt && (
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Revocar"
                        disabled={revokeKey.isPending}
                        onClick={() => {
                          if (confirm(`Revocar la key "${k.name}"? Quien la este usando deja de tener acceso al instante.`)) {
                            revokeKey.mutate(k.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* ---------- Webhooks ---------- */}
        <section>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <Webhook className="h-5 w-5" /> Webhooks
              </h2>
              <p className="text-sm text-muted-foreground">
                Te avisamos por POST firmado cuando pasa algo. Reintenta 4 veces si tu endpoint no responde 2xx.
              </p>
            </div>
            <Button onClick={() => setHookOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Nuevo webhook
            </Button>
          </div>

          {loadingHooks ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando...
            </div>
          ) : hooks.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
              <Webhook className="mx-auto mb-2 h-7 w-7 opacity-50" />
              Sin webhooks. Agrega una URL para recibir los eventos del chat.
            </div>
          ) : (
            <div className="grid gap-3">
              {hooks.map((h) => (
                <Card key={h.id}>
                  <CardContent className="flex flex-wrap items-center gap-3 p-4">
                    <code className="max-w-full overflow-x-auto rounded bg-muted px-2 py-1 text-xs">{h.url}</code>
                    {h.isActive ? <Badge variant="success">Activo</Badge> : <Badge>Pausado</Badge>}
                    <div className="flex flex-wrap gap-1">
                      {h.events.map((e) => (
                        <Badge key={e} variant="outline" className="text-[10px]">{e}</Badge>
                      ))}
                    </div>
                    <span className="ml-auto text-xs text-muted-foreground">
                      secret {h.secretPreview}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={testHook.isPending}
                      onClick={() => testHook.mutate(h.id)}
                    >
                      <Send className="mr-1 h-3 w-3" /> Probar
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Eliminar"
                      disabled={deleteHook.isPending}
                      onClick={() => {
                        if (confirm('Eliminar este webhook? Dejas de recibir esos eventos.')) {
                          deleteHook.mutate(h.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* ---------- Dialog: nueva key ---------- */}
      <Dialog open={keyOpen} onOpenChange={(o) => (o ? setKeyOpen(true) : closeKeyDialog())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{newKey ? 'Key creada' : 'Nueva API key'}</DialogTitle>
            <DialogDescription>
              {newKey
                ? 'Copiala y guardala en un lugar seguro.'
                : 'Dale un nombre que identifique al integrador y elegi solo los permisos que necesita.'}
            </DialogDescription>
          </DialogHeader>

          {newKey ? (
            <SecretOnce value={newKey} label="API key" />
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="keyname">Nombre</Label>
                <Input
                  id="keyname"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="ej: backend de ganamosapp"
                />
              </div>
              <div className="space-y-2">
                <Label>Permisos (scopes)</Label>
                <MultiSelect
                  options={API_SCOPES}
                  selected={keyScopes}
                  onToggle={(v) => toggle(keyScopes, setKeyScopes, v)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {newKey ? (
              <Button onClick={closeKeyDialog}>Listo, ya la guarde</Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeKeyDialog}>Cancelar</Button>
                <Button
                  onClick={submitKey}
                  disabled={createKey.isPending || !keyName.trim() || !keyScopes.length}
                >
                  {createKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Crear key
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Dialog: nuevo webhook ---------- */}
      <Dialog open={hookOpen} onOpenChange={(o) => (o ? setHookOpen(true) : closeHookDialog())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{newSecret ? 'Webhook creado' : 'Nuevo webhook'}</DialogTitle>
            <DialogDescription>
              {newSecret
                ? 'Usa este secret para validar la firma X-Signature de cada POST que te llegue.'
                : 'Los POST llevan la cabecera X-Signature con el HMAC-SHA256 del cuerpo.'}
            </DialogDescription>
          </DialogHeader>

          {newSecret ? (
            <SecretOnce value={newSecret} label="Secret de firma" />
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="hookurl">URL destino</Label>
                <Input
                  id="hookurl"
                  value={hookUrl}
                  onChange={(e) => setHookUrl(e.target.value)}
                  placeholder="https://tu-backend.com/hooks/lucky-chat"
                />
              </div>
              <div className="space-y-2">
                <Label>Eventos</Label>
                <MultiSelect
                  options={WEBHOOK_EVENTS}
                  selected={hookEvents}
                  onToggle={(v) => toggle(hookEvents, setHookEvents, v)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {newSecret ? (
              <Button onClick={closeHookDialog}>Listo, ya lo guarde</Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeHookDialog}>Cancelar</Button>
                <Button
                  onClick={submitHook}
                  disabled={createHook.isPending || !hookUrl.trim() || !hookEvents.length}
                >
                  {createHook.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Crear webhook
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
